import React, { useState } from "react";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import * as S from "./styles";
import { useUserContext } from "../../contexts/user/userContext";
import { updateProfile } from "../../utils/services/modules/user";
import {
  getApiError,
  getApiErrorField,
} from "../../utils/services/database";
import {
  formatCellphone,
  isValidCellphone,
  onlyDigits,
} from "../../utils/user";

interface IPersonalDataForm {
  user: { name: string; cellphone: string; token: string };
}

type Errors = { name?: string; cellphone?: string };

const cleanName = (value: string) => value.trim().replace(/\s+/g, " ");

const PersonalDataForm: React.FC<IPersonalDataForm> = ({ user }) => {
  const { dispatch } = useUserContext();
  const [name, setName] = useState(user.name ?? "");
  const [cellphone, setCellphone] = useState(onlyDigits(user.cellphone));
  const [errors, setErrors] = useState<Errors>({});
  const [saving, setSaving] = useState(false);

  const changedName = cleanName(name) !== cleanName(user.name ?? "");
  const changedPhone = cellphone !== onlyDigits(user.cellphone);
  const dirty = changedName || changedPhone;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    // o botão não é desabilitado (no Chrome isso travava o teclado): a
    // proteção contra clique duplo fica aqui
    if (saving || !dirty) return;

    const nextErrors: Errors = {};
    if (!cleanName(name)) nextErrors.name = "Informe seu nome";
    if (changedPhone && !isValidCellphone(cellphone)) {
      nextErrors.cellphone = "Informe o celular com DDD";
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSaving(true);
    try {
      // só envia o que mudou
      const updated = await updateProfile(
        {
          ...(changedName && { name: cleanName(name) }),
          ...(changedPhone && { cellphone }),
        },
        user.token,
      );
      const profile = {
        name: updated?.name ?? cleanName(name),
        cellphone: updated?.cellphone ?? cellphone,
      };
      dispatch({ type: "UPDATE_PROFILE", payload: profile });
      setName(profile.name);
      setCellphone(onlyDigits(profile.cellphone));
      toast.success("Dados atualizados!");
    } catch (error) {
      const field = getApiErrorField(error);
      const message = getApiError(
        error,
        "Não foi possível salvar. Tente novamente.",
      );
      if (field === "name" || field === "cellphone") {
        setErrors({ [field]: message });
      } else {
        toast.error(message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <S.FormCard onSubmit={handleSubmit} noValidate>
      <S.Field>
        <S.FieldLabel htmlFor="settings-name">Nome</S.FieldLabel>
        <S.Input
          id="settings-name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setErrors((prev) => ({ ...prev, name: undefined }));
          }}
          placeholder="Seu nome"
          autoComplete="name"
          inputProps={{ maxLength: 100 }}
          error={!!errors.name}
          helperText={errors.name}
        />
      </S.Field>

      <S.Field>
        <S.FieldLabel htmlFor="settings-cellphone">Celular</S.FieldLabel>
        <S.Input
          id="settings-cellphone"
          value={formatCellphone(cellphone)}
          onChange={(e) => {
            setCellphone(onlyDigits(e.target.value).slice(0, 11));
            setErrors((prev) => ({ ...prev, cellphone: undefined }));
          }}
          placeholder="(00) 00000-0000"
          autoComplete="tel-national"
          inputProps={{ inputMode: "tel" }}
          error={!!errors.cellphone}
          helperText={errors.cellphone}
        />
      </S.Field>

      <S.SubmitButton
        type="submit"
        $inactive={!dirty}
        aria-disabled={!dirty}
      >
        {saving ? <CircularProgress size={20} color="inherit" /> : "Salvar dados"}
      </S.SubmitButton>
    </S.FormCard>
  );
};

export default PersonalDataForm;
